import { Box, Checkbox, FormHelperText, Grid, Stack, Typography } from "@mui/material";
import palette from "theme/palette";
import { ScopeQuestionSubtypeOption } from "../types";
import { getBadgeStyle } from "../utils";
import SectionLabel from "./SectionLabel";

interface QuestionTypesSectionProps {
  questionSubtypes: ScopeQuestionSubtypeOption[];
  selectedSubtypeKeys: string[];
  watchedQuestionSubtypes: Record<string, boolean>;
  subtypesError?: string;
  onToggleSubtype: (value: string) => void;
}

const QuestionTypesSection = ({
  questionSubtypes,
  selectedSubtypeKeys,
  watchedQuestionSubtypes,
  subtypesError,
  onToggleSubtype,
}: QuestionTypesSectionProps) => {
  return (
    <Box>
      <Stack
        direction='row'
        alignItems='center'
        justifyContent='space-between'
        sx={{ mb: 0.75 }}>
        <SectionLabel sx={{ mb: 0 }}>Типы вопросов</SectionLabel>
        <Stack
          direction='row'
          spacing={0.75}
          alignItems='center'>
          <Typography sx={{ fontSize: 14, color: palette.orange.contrast, minHeight: 16 }}>
            {selectedSubtypeKeys.length >= 3 ? "Максимум 3" : ""}
          </Typography>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: "primary.main" }}>
            {selectedSubtypeKeys.length} / 3
          </Typography>
        </Stack>
      </Stack>
      <Box
        sx={{
          p: 1.25,
          borderRadius: 1.5,
          border: `1px solid ${palette.divider}`,
          backgroundColor: "bgSwitch",
        }}>
        <Grid
          container
          spacing={0.5}>
          {questionSubtypes.map((subtype) => {
            const isSelected = Boolean(watchedQuestionSubtypes?.[subtype.value]);
            const isDisabled = !isSelected && selectedSubtypeKeys.length >= 3;
            const badgeStyle = getBadgeStyle(subtype.badgeType);

            return (
              <Grid
                key={subtype.value}
                item
                xs={12}
                md={6}>
                <Stack
                  component='label'
                  direction='row'
                  sx={{
                    alignItems: "center",
                    gap: 0,
                    p: 1,
                    borderRadius: 3,
                    border: `1.5px solid ${isSelected ? palette.primary.main : palette.divider}`,
                    backgroundColor: isSelected ? palette.third.main : "common.white",
                    opacity: isDisabled ? 0.45 : 1,
                    cursor: isDisabled ? "not-allowed" : "pointer",
                    transition: "border-color .12s ease, background-color .12s ease",
                  }}>
                  <Checkbox
                    checked={isSelected}
                    disabled={isDisabled}
                    onChange={() => onToggleSubtype(subtype.value)}
                    sx={{
                      p: 0,
                      color: palette.divider,
                      flexShrink: 0,
                      "&.Mui-checked": {
                        color: "primary.main",
                      },
                    }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 600,
                        lineHeight: 1.2,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}>
                      {subtype.title}
                    </Typography>
                    {subtype.description && (
                      <Typography
                        sx={{
                          mt: 0.125,
                          fontSize: 14,
                          color: "text.secondary",
                          lineHeight: 1.2,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}>
                        {subtype.description}
                      </Typography>
                    )}
                  </Box>
                  <Box
                    sx={{
                      px: 0.75,
                      py: 0.125,
                      borderRadius: 999,
                      backgroundColor: badgeStyle.backgroundColor,
                      color: badgeStyle.color,
                      fontSize: 14,
                      fontWeight: 700,
                      flexShrink: 0,
                    }}>
                    {subtype.badgeLabel}
                  </Box>
                </Stack>
              </Grid>
            );
          })}
        </Grid>
      </Box>
      {subtypesError && <FormHelperText error>{subtypesError}</FormHelperText>}
    </Box>
  );
};

export default QuestionTypesSection;
